import { DatePickerTime } from 'shared/newEntities/components/DatePicker';

import {
  END_OF_DAY_TIME,
  extractTimeFromDate,
  isFullCalendarDayRange,
  mergeTimeIntoDate,
  START_OF_DAY_TIME
} from './dateTimeUtils';

export const PRESET_MONTH_MONTHS = 1;
export const PRESET_YEAR_MONTHS = 12;

type ComparableDate = {
  date: Date;
  isEqual: (other: ComparableDate) => boolean;
};

type SnapshotPeriodFn = (anchor: Date) => Date;

const isSamePickerTime = (
  left: DatePickerTime,
  right: DatePickerTime
): boolean =>
  left !== null &&
  right !== null &&
  left.hours === right.hours &&
  left.minutes === right.minutes;

const truncateToMinute = (date: Date): Date => {
  const result = new Date(date);

  result.setSeconds(0, 0);

  return result;
};

export const hasMatchingSnapshotTimes = (
  start: Date,
  end: Date,
  now: Date = new Date()
): boolean => {
  const nowTime = extractTimeFromDate(now);
  const endTime = extractTimeFromDate(end);
  const startTime = extractTimeFromDate(start);

  return (
    isSamePickerTime(endTime, nowTime) && isSamePickerTime(startTime, endTime)
  );
};

export const getRangeBoundaries = (
  first: ComparableDate,
  second: ComparableDate,
  withTime: boolean
): { start: Date; end: Date } => ({
  start: withTime
    ? first.date
    : mergeTimeIntoDate(first.date, START_OF_DAY_TIME),
  end: withTime ? second.date : mergeTimeIntoDate(second.date, END_OF_DAY_TIME)
});

export const isCalendarDayPresetActive = (
  first: ComparableDate | null,
  second: ComparableDate | null,
  day: Date,
  withTime: boolean
): boolean => {
  if (!first || !second) {
    return false;
  }

  const { start, end } = getRangeBoundaries(first, second, withTime);

  return isFullCalendarDayRange(start, end, day);
};

export const isSameDayDateOnlyPresetActive = (
  first: ComparableDate | null,
  second: ComparableDate | null,
  day: Date,
  createModel: (date: Date) => ComparableDate
): boolean => {
  if (!first || !second) {
    return false;
  }

  const model = createModel(day);

  return first.isEqual(model) && second.isEqual(model);
};

export const isSnapshotPresetActive = (
  first: ComparableDate | null,
  second: ComparableDate | null,
  subtractPeriod: SnapshotPeriodFn,
  createModel: (date: Date) => ComparableDate,
  withTime: boolean,
  now: Date = new Date()
): boolean => {
  if (!first || !second) {
    return false;
  }

  if (withTime) {
    const { start, end } = getRangeBoundaries(first, second, true);

    if (!hasMatchingSnapshotTimes(start, end, now)) {
      return false;
    }

    const expectedStart = subtractPeriod(now);
    const expectedEnd = now;

    return (
      truncateToMinute(start).getTime() ===
        truncateToMinute(expectedStart).getTime() &&
      truncateToMinute(end).getTime() ===
        truncateToMinute(expectedEnd).getTime()
    );
  }

  const endModel = createModel(now);
  const startModel = createModel(subtractPeriod(now));

  return first.isEqual(startModel) && second.isEqual(endModel);
};
